/**
 * 8 Ways to Research With Perplexity: /resources/perplexity/8-ways
 *
 * Caren's Perplexity one-pager, rebuilt as the same interactive document the
 * Claude and Gemini Notebook paths already use: eight cards, one open at a
 * time, each with its own "got it" and a progress bar across the top.
 *
 * The stylesheet is the one from the other 8-ways guides, unchanged. Copying
 * it by hand and adjusting as I went is exactly how fifteen tool pages ended
 * up with 2,460 lines of divergent inline style, so it is lifted whole.
 *
 * The eight ways, their order and their wording are the PDF's.
 */

import GuideShell from "@/components/GuideShell";
import GuideFrame from "@/components/GuideFrame";

const HTML_SRC = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>8 Ways to Research With Perplexity | Your Smart Stack</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">

<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --cream: oklch(0.98 0.008 85);
    --linen: oklch(0.93 0.01 85);
    --espresso: oklch(0.22 0.015 65);
    --terracotta: oklch(0.32 0.08 255);
    --border: oklch(0.88 0.012 85);
    --white: oklch(1 0.004 85);
    --terracotta-light: oklch(0.32 0.08 255 / 0.08);
    --terracotta-mid: oklch(0.32 0.08 255 / 0.22);
    --muted: oklch(0.52 0.015 65);
    --body: oklch(0.52 0.015 65);
  }

  body {
    background: var(--cream);
    color: var(--espresso);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    min-height: 100vh;
    padding: 40px 20px 60px;
  }

  .container { max-width: 680px; margin: 0 auto; }

  .header {
    text-align: center;
    margin-bottom: 48px;
    padding-bottom: 36px;
    border-bottom: 1px solid var(--border);
  }

  .header .eyebrow {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 14px;
  }

  .header h1 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: clamp(28px, 5vw, 38px);
    font-weight: 700;
    color: var(--espresso);
    line-height: 1.2;
    margin-bottom: 14px;
  }

  .header p {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 480px;
    margin: 0 auto 20px;
  }

  .progress-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 20px;
  }

  .progress-bar {
    width: 200px;
    height: 4px;
    background: var(--border);
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: var(--terracotta);
    border-radius: 2px;
    transition: width 0.5s ease;
    width: 0%;
  }

  .progress-text {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: oklch(0.52 0.015 65);
  }

  .step {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 3px;
    margin-bottom: 16px;
    overflow: hidden;
    transition: all 0.3s ease;
  }

  .step.completed {
    border-color: var(--terracotta-mid);
    background: var(--terracotta-light);
  }

  .step-header {
    display: flex;
    align-items: flex-start;
    gap: 18px;
    padding: 24px 28px;
    cursor: pointer;
    user-select: none;
  }

  .step-check {
    width: 28px;
    height: 28px;
    border: 2px solid var(--border);
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    background: var(--white);
  }

  .step.completed .step-check {
    background: var(--terracotta);
    border-color: var(--terracotta);
  }

  .step-check svg { opacity: 0; transform: scale(0.5); transition: all 0.25s ease; }
  .step.completed .step-check svg { opacity: 1; transform: scale(1); }

  .step-title-wrap { flex: 1; }

  .step-number {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 4px;
  }

  .step.completed .step-number { color: oklch(0.26 0.075 255); }

  .step-title {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 20px;
    font-weight: 600;
    color: var(--espresso);
    line-height: 1.3;
  }

  .step-toggle {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 20px;
    color: var(--border);
    flex-shrink: 0;
    line-height: 1;
    margin-top: 4px;
    transition: transform 0.3s ease, color 0.3s ease;
  }

  .step.open .step-toggle { transform: rotate(45deg); color: var(--terracotta); }

  .step-body { display: none; padding: 0 28px 28px 74px; }
  .step.open .step-body { display: block; }

  .step-body p {
    font-size: 16px;
    line-height: 1.7;
    color: var(--body);
    margin-bottom: 14px;
  }

  .step-body ul { list-style: none; margin-bottom: 16px; }

  .step-body ul li {
    font-size: 16px;
    line-height: 1.6;
    color: var(--body);
    padding: 5px 0 5px 20px;
    position: relative;
  }

  .step-body ul li::before {
    content: "—";
    position: absolute;
    left: 0;
    color: var(--terracotta);
    font-weight: 300;
  }

  .principle-box {
    background: var(--linen);
    border-left: 3px solid var(--terracotta);
    border-radius: 0 3px 3px 0;
    padding: 14px 18px;
    margin: 12px 0 16px;
  }

  .principle-box .label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 6px;
  }

  .principle-box p {
    font-size: 14px;
    color: var(--espresso);
    line-height: 1.6;
    margin: 0;
    font-style: italic;
  }

  .mark-done-btn {
    display: inline-block;
    margin-top: 8px;
    padding: 10px 22px;
    background: var(--terracotta);
    color: var(--white);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.03em;
    border: none;
    border-radius: 2px;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .mark-done-btn:hover { background: oklch(0.26 0.075 255); }
  .step.completed .mark-done-btn { background: oklch(0.26 0.075 255); }

  .tip-box {
    background: var(--linen);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 24px 28px;
    margin-top: 8px;
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .tip-icon {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--terracotta);
  }

  .tip-box p {
    font-size: 16px;
    line-height: 1.7;
    color: var(--body);
    margin: 0;
  }

  .completion {
    display: none;
    text-align: center;
    padding: 40px 28px;
    background: var(--white);
    border: 1px solid var(--terracotta-mid);
    border-radius: 3px;
    margin-top: 8px;
  }

  .completion.visible { display: block; }

  .completion .icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 16px;
    color: var(--terracotta);
  }

  .completion h2 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 24px;
    color: var(--espresso);
    margin-bottom: 10px;
  }

  .completion p {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 420px;
    margin: 0 auto 20px;
  }

  .completion a {
    display: inline-block;
    padding: 12px 28px;
    background: var(--terracotta);
    color: var(--white);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    border-radius: 2px;
    text-decoration: none;
    transition: background 0.2s;
  }

  .completion a:hover { background: oklch(0.26 0.075 255); }

  .footer {
    text-align: center;
    margin-top: 48px;
    padding-top: 28px;
    border-top: 1px solid var(--border);
  }

  .footer p {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: oklch(0.88 0.012 85);
    letter-spacing: 0.04em;
  }

  .footer a { color: var(--terracotta); text-decoration: none; }
</style>
</head>
<body>
<div class="container">

  <div class="header">
    <p class="eyebrow">Resource Center: Smart Stack</p>
    <h1>8 Ways to Research<br>With Perplexity</h1>
    <p>Because the goal was never more information: it was a clearer decision.</p>
    <div class="progress-wrap">
      <div class="progress-bar"><div class="progress-fill" id="progressFill"></div></div>
      <span class="progress-text" id="progressText">0 of 8 complete</span>
    </div>
  </div>

  <!-- WAY 1 -->
  <div class="step open" id="step1">
    <div class="step-header" onclick="toggleStep('step1')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 01</div>
        <div class="step-title">Know your question before you search</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Perplexity returns better answers when you give it a better question. Before you type, name what you actually need to know: not the topic, the specific question. That one shift changes what comes back.</p>
      <div class="principle-box">
        <div class="label">The Shift</div>
        <p>A topic gets you a summary. A question gets you an answer you can act on.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step1')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 2 -->
  <div class="step" id="step2">
    <div class="step-header" onclick="toggleStep('step2')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 02</div>
        <div class="step-title">Define “enough” before you open the tab</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Research expands to fill available time. Decide in advance what a sufficient answer looks like, and set the bar before you start rather than after you are tired.</p>
      <ul>
        <li>One good source?</li>
        <li>Three confirming patterns?</li>
        <li>A clear yes or no?</li>
      </ul>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>Set the bar first. Stop when you reach it.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step2')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 3 -->
  <div class="step" id="step3">
    <div class="step-header" onclick="toggleStep('step3')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 03</div>
        <div class="step-title">Read the answer, not all the sources</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Perplexity synthesizes so you don't have to read everything. Trust the synthesis first.</p>
      <p>Open a source only when a specific claim needs verification. Not out of habit. Not out of anxiety.</p>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>Verification is for specific claims, not for reassurance.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step3')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 4 -->
  <div class="step" id="step4">
    <div class="step-header" onclick="toggleStep('step4')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 04</div>
        <div class="step-title">Ask one question at a time</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Compound questions produce diluted answers. If you have three things you need to know, ask them in sequence. Each answer sharpens the next question.</p>
      <div class="principle-box">
        <div class="label">The Shift</div>
        <p>That's research, not search.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step4')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 5 -->
  <div class="step" id="step5">
    <div class="step-header" onclick="toggleStep('step5')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 05</div>
        <div class="step-title">Use Pro Search for complexity, not convenience</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Pro Search runs deeper and slower. Save it for questions that actually need multi-step reasoning: decisions with real stakes, topics with real nuance.</p>
      <p>For quick orientation, a standard search is enough.</p>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>Depth on demand, not depth by default.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step5')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 6 -->
  <div class="step" id="step6">
    <div class="step-header" onclick="toggleStep('step6')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 06</div>
        <div class="step-title">Stop when themes repeat</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>When three different sources say the same thing, you have your answer. Continuing past that point is avoidance, not research.</p>
      <p>Notice the repetition. Close the tab. Decide.</p>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>Repetition is the signal that you are done.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step6')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 7 -->
  <div class="step" id="step7">
    <div class="step-header" onclick="toggleStep('step7')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 07</div>
        <div class="step-title">Bring the insight out, don't stay inside</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Perplexity is where you gather. Your thinking tool is where you decide. The mistake is staying in Perplexity too long.</p>
      <p>Extract what you need. Move to the tool where you think.</p>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>One tool retrieves. Another reasons. Keep the jobs separate.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step7')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 8 -->
  <div class="step" id="step8">
    <div class="step-header" onclick="toggleStep('step8')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 08</div>
        <div class="step-title">Use a Space when a topic has a life</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>One-off searches are for one-off questions. When a topic keeps coming back — a competitor, a project, a market — build a Space.</p>
      <p>Let it accumulate. Let the context compound.</p>
      <div class="principle-box">
        <div class="label">Smart Stack Principle</div>
        <p>Context stored is context you don't have to rebuild.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step8')">✓ Got it</button>
    </div>
  </div>

  <!-- TIP -->
  <div class="tip-box">
    <svg class="tip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26C17.81 13.47 19 11.38 19 9c0-3.87-3.13-7-7-7z"/>
      <path d="M9 21h6M10 17v-2M14 17v-2"/>
    </svg>
    <p><strong>The short version:</strong> name the question before you type it, decide what "enough" looks like, trust the synthesis, and stop when the themes repeat. Perplexity is where you get oriented. It is not where you decide.</p>
  </div>

  <!-- COMPLETION -->
  <div class="completion" id="completion">
    <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
    <h2>You don't need more information.</h2>
    <p>You need a better research discipline. Orient quickly, stop early, and trust your judgment about when you have enough.</p>
    <a href="https://aitools.yoursmartstack.com/" target="_blank">Explore the Resource Center →</a>
  </div>


</div>

<script>
  const steps = ['step1','step2','step3','step4','step5','step6','step7','step8'];

  function toggleStep(id) {
    const el = document.getElementById(id);
    const isOpen = el.classList.contains('open');
    steps.forEach(s => document.getElementById(s).classList.remove('open'));
    if (!isOpen) el.classList.add('open');
  }

  function markDone(id) {
    const el = document.getElementById(id);
    el.classList.add('completed');
    el.classList.remove('open');
    updateProgress();
  }

  function updateProgress() {
    const done = steps.filter(s => document.getElementById(s).classList.contains('completed')).length;
    const pct = (done / steps.length) * 100;
    document.getElementById('progressFill').style.width = pct + '%';
    document.getElementById('progressText').textContent = done + ' of ' + steps.length + ' complete';
    if (done === steps.length) {
      document.getElementById('completion').classList.add('visible');
    }
  }

  /* No toggleStep('step1') call here, deliberately. Step 1 already carries the
     open class in the markup, so calling toggle CLOSES it — every one of these
     guides loaded with all its cards shut and nothing to show that a card opens
     at all. The markup is the intent. Fixed 4 Sep 2026. */
<\/script>
</body>
</html>
`;

export default function PerplexityEightWays() {
  return (
    <GuideShell
      context={"Perplexity guide"}
      backHref={"/resources/perplexity"}
      backLabel={"Back to Perplexity"}
    >
      <GuideFrame html={HTML_SRC} title={"8 Ways to Research With Perplexity"} />
    </GuideShell>
  );
}
